import axios from "axios";
import { API_HOST } from "../constants";

// Base API configuration
class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_HOST,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const authToken = localStorage.getItem("auth-token");
        if (authToken) {
          config.headers["Authorization"] = `Bearer ${authToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem("auth-token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  // Generic HTTP methods
  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  // User API methods
  async createUser(userData) {
    return this.post("/user/create_user", userData);
  }

  async loginUser(credentials) {
    return this.post("/user/login", credentials);
  }

  async updateUser(userData) {
    return this.put("/user/update", userData);
  }

  async getUser() {
    return this.get("/user/get_user");
  }

  async getUserPreferences() {
    return this.get("/user/get_preferences");
  }

  async updateBiomonitoring(bioData) {
    return this.put("/user/update_biomonitoring", bioData);
  }

  async getOnboardingStatus() {
    return this.get("/user/onboarding/status");
  }

  async submitOnboarding(data) {
    return this.post("/user/onboarding/submit", data);
  }

  async logoutUser() {
    return this.post("/user/logout");
  }

  // Meal Planning API methods
  async generateMealPlan(planData) {
    return this.post("/mealplan/generate_plan", planData);
  }

  async getUserMealPlans() {
    return this.get("/mealplan/get_user_plans");
  }

  async saveMealPlan(planData) {
    return this.post("/mealplan/save_plan", planData);
  }

  async getMealPlanDetails(planId) {
    return this.get(`/mealplan/get_plan_details?plan_id=${planId}`);
  }

  async getGoals() {
    return this.get("/mealplan/get_goals");
  }

  async createGoal(goalData) {
    return this.post("/mealplan/create_goal", goalData);
  }

  async deleteGoal(goalData) {
    return this.delete("/mealplan/delete_goal", { data: goalData });
  }

  async getFoods() {
    return this.get("/mealplan/get_foods");
  }

  async getAllergies() {
    return this.get("/mealplan/get_allergies");
  }

  async createAllergy(allergyData) {
    return this.post("/mealplan/create_allergy", allergyData);
  }

  // Grocery Cart API methods
  async getGroceryCart() {
    return this.get("/grocery/get_cart");
  }

  async addToGroceryCart(itemData) {
    return this.post("/grocery/add_to_cart", itemData);
  }

  async removeFromGroceryCart(itemData) {
    return this.delete("/grocery/remove_from_cart", { data: itemData });
  }

  async clearGroceryCart() {
    return this.delete("/grocery/clear_cart");
  }

  // Waitlist
  async joinWaitlist(data) {
    return this.post("/waitlist/join", data);
  }

  // AI Integration methods (for future implementation)
  async generateAIMealPlan(preferences) {
    // This would integrate with an external AI service
    // For now, using the backend mock
    return this.generateMealPlan(preferences);
  }

  async getRecipeSuggestions(preferences) {
    // Future AI integration for recipe suggestions
    return this.post("/ai/recipe_suggestions", preferences);
  }

  async analyzeNutrition(foodData) {
    // Future AI integration for nutrition analysis
    return this.post("/ai/nutrition_analysis", foodData);
  }
}

const apiService = new ApiService();
export default apiService;
